import React from 'react';
import { Redirect } from 'react-router-dom';
import getPath from 'helpers/path';
import HotjarService from 'helpers/hotjar';
import menuConfiguration from 'constants/menuConfiguration';
import urlsMap from 'constants/urlsMap';
import { Route } from './routeTypes';

import Root from 'pages/root';
import Profile from 'pages/profile';
import Payments from 'pages/payments';
import Payouts from 'pages/payouts';
import LcManage from 'pages/lcmanage';
import LcManageUpload from 'pages/lcmanage/upload';
import Reports from 'pages/reports';
import Finance from 'pages/finance';
import Projects from 'pages/projects';
import Invoices from 'pages/invoices';
import Recurring from 'pages/recurring';
import Risk from 'pages/risks/RisksContainer';
import References from 'pages/references';
import Chargebacks from 'pages/chargebacks';
import Analytics from 'pages/analytics';
import Team from 'pages/team';
import UserBuilder from 'components/userBuilder';
import BusinessDocuments from 'pages/businessDocuments';
import Icons from 'pages/icons';
import UserManage from 'pages/admin';
import Help from 'pages/help';
import Reconciliation from 'pages/reconciliation';
import AdminTools from 'pages/adminTools';
import GetStarted from 'pages/getStarted';
import Remittance from 'pages/remittance';
import AdditionalFeature from 'pages/getStarted/overview/components/additionalFeature';
import FinancialData from 'pages/financialData';
import SettlementData from 'pages/settlement';
import BalanceManage from 'pages/balanceManage';
import Messages from 'constants/rpcTypes';

const privateRoutesConfig: Route[] = [
  {
    path: getPath('/'),
    component: Root,
    exact: true,
    params: {
      permissions: [
        Messages.Payments_List,
        Messages.Maf_Get,
        Messages.DemoStage_Project,
        Messages.Profile_Get,
      ],
    },
  },
  {
    path: getPath('/profile'),
    component: Profile,
    params: {
      permissions: [Messages.Profile_Get],
    },
  },
  {
    path: getPath('/payments/:id?'),
    component: Payments,
  },
  {
    path: getPath(`/${urlsMap.payouts}/request/:formId`),
    component: Payouts,
  },
  {
    path: getPath(`/${urlsMap.payouts}/:id?`),
    component: Payouts,
  },
  {
    path: getPath('/lcmanage/upload'),
    component: LcManageUpload,
  },
  {
    path: getPath('/lcmanage'),
    component: LcManage,
  },
  {
    path: getPath('/maf/:id?'),
    component: BusinessDocuments,
  },
  {
    path: getPath('/reports/create'),
    component: Reports,
    params: {
      isTemplatesBuilder: true,
    },
  },
  {
    path: getPath('/reports/:id'),
    component: Reports,
    params: {
      isTemplatesBuilder: true,
    },
  },
  {
    path: getPath('/reports/templates'),
    component: Reports,
    params: {
      isTemplatesList: true,
    },
  },
  {
    path: getPath('/reports'),
    component: Reports,
  },
  {
    path: getPath('/get-started/features'),
    component: AdditionalFeature,
  },
  {
    path: getPath('/get-started'),
    component: GetStarted,
  },

  {
    path: getPath('/analytics/:slug?'),
    component: Analytics,
  },
  {
    path: getPath('/analytics'),
    component: Analytics,
  },
  {
    path: getPath('/admin'),
    exact: true,
    component: UserManage,
    params: {
      isAdmin: true,
    },
  },
  {
    path: getPath('/admin/create'),
    component: UserBuilder,
    params: {
      isAdmin: true,
    },
  },
  {
    path: getPath('/admin/edit/:id'),
    component: UserBuilder,
    params: {
      isAdmin: true,
    },
  },
  {
    path: getPath('/team/create'),
    component: UserBuilder,
  },
  {
    path: getPath('/team/edit/:id'),
    component: UserBuilder,
  },
  {
    path: getPath('/team'),
    component: Team,
  },
  {
    path: getPath('/finance/:id'),
    component: Finance,
  },
  {
    path: getPath('/finance'),
    component: Finance,
  },
  {
    path: getPath('/recurring-payments/:id?'),
    component: Recurring,
  },
  {
    path: getPath('/projects/:id?'),
    component: Projects,
  },
  {
    path: getPath('/invoices/:formId'),
    component: Invoices,
    params: {
      isCreate: true,
    },
  },
  {
    path: getPath('/invoices'),
    component: Invoices,
  },
  {
    path: getPath('/references'),
    component: References,
  },
  {
    path: getPath('/risks/:id?'),
    component: Risk,
  },
  {
    path: getPath('/chargebacks/:id?'),
    component: Chargebacks,
  },
  {
    path: getPath('/icons'),
    component: Icons,
  },
  {
    path: getPath('/admin-tools'),
    component: AdminTools,
  },
  {
    path: getPath('/admin-fin-data'),
    component: FinancialData,
  },
  /* {
    path: getPath('/help/search'),
    component: Help,
    params: {
      isSearchResults: true,
    },
    key: 'help',
  }, */
  {
    path: getPath('/help/:slug?'),
    component: Help,
    key: 'help',
    params: {
      permissions: [Messages.Article_Menu],
    },
  },
  {
    path: getPath('/reconciliation'),
    component: Reconciliation,
    key: 'reconciliation',
  },
  {
    path: getPath(`/${urlsMap.remittance}/:id?/:formId?`),
    component: Remittance,
    key: 'remittance',
  },
  {
    path: getPath('/settlements'),
    component: SettlementData,
    params: {
      permissions: [Messages.Settlement_List],
    },
  },
  {
    path: getPath('/balance-manage'),
    component: BalanceManage,
    params: {
      permissions: [Messages.BalanceManage_List],
    },
  },
  {
    component: () => <Redirect to={getPath('')} />,
  },
];

const setPermissions = (route: Route) => {
  if (route.params?.permissions) {
    return route.params;
  }

  const routePath = route.path?.split('/')[1];
  const paramsItem = menuConfiguration.find((item) => {
    if (item.customUrl) {
      return item.customUrl === routePath;
    }
    return item.key === routePath;
  });

  return {
    ...route.params,
    permissions: paramsItem?.permissions,
  };
};

const setSettings = (): Route[] => {
  return privateRoutesConfig.map((route): Route => {
    return {
      ...route,
      params: { ...setPermissions(route) },
      component: HotjarService.routeChangeStats(route.component),
    };
  });
};

const privateRoutes: Route[] = setSettings();

export default privateRoutes;
